import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { Active, UniqueIdentifier } from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { Box, Grid2 } from '@mui/material';
import { SortableOverlay } from '../SortableOverlay';
import { GridDragHandle, SortableGridItem } from './SortableGridItem';

interface BaseItem {
  id: UniqueIdentifier;
}

interface Props<T extends BaseItem> {
  items: T[];
  onChange(items: T[]): void;
  renderItem(item: T): ReactNode;
  itemSize?: { xs?: number; sm?: number; md?: number; lg?: number };
  minItemWidth?: number;
  trailing?: ReactNode;
}

export function SortableGrid<T extends BaseItem>({
  items,
  onChange,
  renderItem,
  itemSize = { xs: 12, sm: 6, md: 4, lg: 3 },
  minItemWidth,
  trailing,
}: Props<T>) {
  const [active, setActive] = useState<Active | null>(null);
  const activeItem = useMemo(
    () => items.find(item => item.id === active?.id),
    [active, items],
  );
  const sensors = useSensors(
    useSensor(PointerSensor, {}),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={({ active: newActive }) => {
        setActive(newActive);
      }}
      onDragEnd={({ active: newActive, over }) => {
        if (over && newActive.id !== over.id) {
          const activeIndex = items.findIndex(({ id }) => id === newActive.id);
          const overIndex = items.findIndex(({ id }) => id === over.id);
          onChange(arrayMove(items, activeIndex, overIndex));
        }
        setActive(null);
      }}
      onDragCancel={() => {
        setActive(null);
      }}>
      <SortableContext items={items} strategy={rectSortingStrategy}>
        {minItemWidth ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(auto-fill, minmax(${minItemWidth}px, 1fr))`,
              gap: 1.25,
            }}>
            {items.map(item => (
              <SortableGridItem key={item.id} id={item.id}>
                {renderItem(item)}
              </SortableGridItem>
            ))}
            {trailing}
          </Box>
        ) : (
          <Grid2 container spacing={2.5} columns={12}>
            {items.map(item => (
              <Grid2 key={item.id} size={itemSize}>
                <SortableGridItem id={item.id}>
                  {renderItem(item)}
                </SortableGridItem>
              </Grid2>
            ))}
          </Grid2>
        )}
      </SortableContext>
      <SortableOverlay>
        {activeItem ? (
          <Box sx={{ pointerEvents: 'none' }}>{renderItem(activeItem)}</Box>
        ) : null}
      </SortableOverlay>
    </DndContext>
  );
}

SortableGrid.Item = SortableGridItem;
SortableGrid.DragHandle = GridDragHandle;
